import { auth, setAmplitudeUserProperty } from 'gatsby-theme-apollo';
import React, { useEffect } from 'react';
import { gql } from '@apollo/client';
import { useLocalStorage } from '../hooks/use-local-storage';
import { apolloClientInstance } from 'gatsby-theme-apollo';
import { canUseLocalStorage } from '../util/canuse';

const AuthorContext = React.createContext();

const AuthorProvider = ({ children }) => {
  const [author, setAuthor] = useLocalStorage('author');

  const retrieveAuthorForSub = async sub => {
    const queryResult = await apolloClientInstance
      .query({
        query: gql`
          {
            authors (limit: 1, 
              where : { 
              authid_eq: "${sub}"
          }) {
              id
              name
              authid
            }
          }
        `
      })
      .then(result => {
        if (!result.data || !result.data.authors || result.data.authors.length === 0) {
          return;
        }

        const retrievedAuthor = result.data.authors && result.data.authors[0];
        return retrievedAuthor;
      })
      .catch(error => console.error(error));
    return queryResult;
  };

  const isAuthorNameAvailable = async ({ name }) => {
    const queryResult = await apolloClientInstance
      .query({
        query: gql`
          query {
            authors (where: {name_contains: "${name}"}) {
              name
              id
              authid
            }
          }
        `
      })
      .then(result => {
        if (!result.data || !result.data.authors || result.data.authors.length === 0) {
          return true;
        }

        // The signed in author has a capitalization-agnostic variant of this name
        const signedInUserHasNameWithSameCharacters = result.data.authors.some(
          ({ name: existingAuthorName, authid }) =>
            existingAuthorName.toLowerCase() === author.name && authid === author.authid
        );

        if (signedInUserHasNameWithSameCharacters) return true;

        const nameLowerCase = name.toLowerCase();

        return result.data.authors.every(
          existingAuthor => existingAuthor.name.toLowerCase() !== nameLowerCase
        );
      })
      .catch(error => {
        return false;
      });

    return queryResult;
  };

  const getCachedAuthor = sub => {
    if (canUseLocalStorage()) {
      const cachedAuthor = localStorage.getItem(sub);
      if (cachedAuthor) {
        return JSON.parse(cachedAuthor);
      }
    }
  };

  const getAuthor = async sub => {
    const cachedAuthor = getCachedAuthor(sub);
    if (cachedAuthor) {
      return cachedAuthor;
    }
    return await retrieveAuthorForSub(sub);
  };

  const SignOut = () => {
    setAuthor(null);
    auth.logout();
  };

  const SignIn = () => {
    auth.login();
  };

  const ChangeName = newName => {
    const newAuthor = {
      ...author,
      name: newName
    };
    setAuthor(newAuthor);
    setAmplitudeUserProperty('author', newAuthor);
  };

  const TrySetAuthorIfUnset = () => {
    if (!author) {
      if (auth.isAuthenticated()) {
        const user = auth.getUser();
        if (user) {
          getAuthor(user.sub).then(authorResult => {
            setAuthor(authorResult);
            setAmplitudeUserProperty('author', authorResult);
          });
        }
      }
    }
  };

  // 1. Try to load author from cache
  // 2. If none found, make roundtrip to retrieve
  // 3.   Success -> Store

  useEffect(() => {
    TrySetAuthorIfUnset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [author]);

  return (
    <AuthorContext.Provider value={{ author, SignOut, SignIn, ChangeName, isAuthorNameAvailable }}>
      {children}
    </AuthorContext.Provider>
  );
};

export { AuthorContext, AuthorProvider };
